import React, { useState } from 'react';
import { Building2, UserCheck, Mail, Tag, Award, Layers, LineChart, BarChart3, Plug } from 'lucide-react';
import { Card, Button } from '../ui';
import OrganizationList from './OrganizationList';
import UserList from './UserList';
import PendingInvitations from './PendingInvitations';
import CategoriesList from './CategoriesList';
import TiersList from './TiersList';
import FeatureAdoptionView from './FeatureAdoptionView';
import EnhancedApiAdoptionView from './EnhancedApiAdoptionView';
import SystemDashboard from './SystemDashboard';
import SystemContentPerformance from './SystemContentPerformance';

type SupportView = 'overview' | 'organizations' | 'users' | 'pending-invitations' | 'categories' | 'tiers' | 'feature-adoption' | 'enhanced-api' | 'system-dashboard' | 'content-performance';

const SupportDashboard: React.FC = () => {
  const [currentView, setCurrentView] = useState<SupportView>('overview');



  if (currentView === 'organizations') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <OrganizationList />
      </div>
    );
  }

  if (currentView === 'users') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <UserList />
      </div>
    );
  }

  if (currentView === 'pending-invitations') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <PendingInvitations />
      </div>
    );
  }

  if (currentView === 'categories') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <CategoriesList />
      </div>
    );
  }

  if (currentView === 'tiers') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <TiersList />
      </div>
    );
  }

  if (currentView === 'feature-adoption') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <FeatureAdoptionView />
      </div>
    );
  }

  if (currentView === 'enhanced-api') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <EnhancedApiAdoptionView />
      </div>
    );
  }

  if (currentView === 'system-dashboard') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setCurrentView('overview')}
          >
            ← Back to Support
          </Button>
        </div>
        <SystemDashboard />
      </div>
    );
  }

  if (currentView === 'content-performance') {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setCurrentView('overview')}>
            ← Back to Support
          </Button>
        </div>
        <SystemContentPerformance />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Support Dashboard</h1>
        <p className="text-gray-600">Manage organizations and users</p>
      </div>

      {/* Support Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('system-dashboard')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <LineChart className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">System Dashboard</h4>
              <p className="text-sm text-gray-500">Platform-wide social metrics</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Aggregate EMV, posts & trends across all active tenants (filter by tenant)</p>
        </Card>

        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('content-performance')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Portfolio Value</h4>
              <p className="text-sm text-gray-500">Category × format heatmap (Brand Scout)</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">EMV by content category & format across active tenants; drill into posts & detected brands</p>
        </Card>

        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('organizations')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Building2 className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Organizations</h4>
              <p className="text-sm text-gray-500">Manage organizations & subscriptions</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Create, edit organizations and their customers</p>
        </Card>

        <Card 
          variant="outlined" 
          hover 
          className="cursor-pointer"
          onClick={() => setCurrentView('users')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCheck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">User Management</h4>
              <p className="text-sm text-gray-500">Manage user accounts & permissions</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Create, edit, and manage user accounts across organizations</p>
        </Card>



        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('pending-invitations')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Mail className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Pending Invitations</h4>
              <p className="text-sm text-gray-500">Manage user invitations</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">View and resend pending user invitations</p>
        </Card>

        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('categories')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Tag className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Categories</h4>
              <p className="text-sm text-gray-500">Manage partner categories</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Create and manage partner category classifications</p>
        </Card>

        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('tiers')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Award className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Tiers</h4>
              <p className="text-sm text-gray-500">Manage partner tiers</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">Create and manage partner tier levels</p>
        </Card>

        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('feature-adoption')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-violet-100 rounded-lg">
              <Layers className="h-5 w-5 text-violet-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Feature Adoption</h4>
              <p className="text-sm text-gray-500">View organizations by enabled feature</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">See which organizations have a feature enabled along with their limits</p>
        </Card>

        <Card
          variant="outlined"
          hover
          className="cursor-pointer"
          onClick={() => setCurrentView('enhanced-api')}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className="p-2 bg-sky-100 rounded-lg">
              <Plug className="h-5 w-5 text-sky-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">Enhanced API</h4>
              <p className="text-sm text-gray-500">Instagram Graph API connections</p>
            </div>
          </div>
          <p className="text-xs text-gray-400">See which organizations and social accounts are connected, with enable & token expiration dates</p>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card variant="elevated">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Organizations</h3>
          <p className="text-3xl font-bold text-avi-primary">--</p>
          <p className="text-sm text-gray-500">Loading...</p>
        </Card>

        <Card variant="elevated">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Users</h3>
          <p className="text-3xl font-bold text-avi-accent">--</p>
          <p className="text-sm text-gray-500">Loading...</p>
        </Card>
      </div>
    </div>
  );
};

export default SupportDashboard;